import { useTranslation } from "react-i18next";
import { Reveal } from "@/components/ui/reveal";
import LocaleLink from "@/components/LocaleLink";
import { ArrowRight } from "lucide-react";
import TrustBadge from "@/components/TrustBadge";
import { tier1Signals, tier2Signals, tier3Signals } from "@/data/trustSignals";

/**
 * The certification strip.
 *
 * This used to be a full light section: eyebrow, section-scale heading, lead
 * paragraph, an eight-column badge grid and two large buttons. That is a lot of
 * page for what is really a credibility rail — the badges are the content and
 * everything around them was framing.
 *
 * It is now a compact Brand Ink strip. Dark because it sits between two light
 * sections and reads as a rule across the middle of the page rather than
 * another destination, and because the badges hold their own colour better on
 * ink than on white. TrustBadge's "strip" variant drops the card chrome; the
 * old "logo" variant is no longer referenced anywhere.
 *
 * certConstellation.subtitle is no longer rendered — the Trust Center link
 * makes the same offer in four words.
 */
const CertificationConstellation = () => {
  const { t } = useTranslation();
  const allSignals = [...tier1Signals, ...tier2Signals, ...tier3Signals];

  return (
    <section
      className="border-y border-brand-ink bg-brand-ink py-10 text-brand-ink-foreground"
      id="certifications"
    >
      <div className="container mx-auto px-6">
        <Reveal>
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-trail-teal">
              {t("certConstellation.eyebrow")}
            </p>
            <h2 className="text-balance text-lg font-semibold text-brand-ink-foreground sm:text-xl">
              {t("certConstellation.title")}
            </h2>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-7 flex flex-wrap items-start justify-center gap-x-3 gap-y-5">
            {allSignals.map((signal) => (
              <TrustBadge key={signal.id} signal={signal} variant="strip" />
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <LocaleLink
              to="/trust"
              className="inline-flex items-center gap-1.5 font-medium text-trail-teal hover:underline"
            >
              {t("certConstellation.cta")}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </LocaleLink>
            <LocaleLink
              to="/data-security"
              className="text-brand-ink-foreground/70 hover:text-brand-ink-foreground hover:underline"
            >
              {t("certConstellation.security")}
            </LocaleLink>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CertificationConstellation;
